import { DEFAULT_IMAGE_DENSITY } from '../components/ImageDensityWidget';
import {
  attachHeatmapPositionColumns,
  inferPositionKeyColumn,
  isHeatmapPositionColumn,
  normalizeMarkerValueAggregation,
} from './imageDensityPoints';

const MARKER_SHAPES = new Set(['circle', 'square', 'pin', 'diamond']);

function normalizeMarkerShape(shape) {
  return MARKER_SHAPES.has(shape) ? shape : 'circle';
}

function normalizeMarkerFit(fit) {
  return fit === 'marker' ? 'marker' : 'text';
}

const MARKER_SIZES = new Set(['small', 'medium', 'large']);

export function normalizeMarkerSize(size) {
  return MARKER_SIZES.has(size) ? size : 'medium';
}

function normalizeMarkerColor(color) {
  const raw = String(color || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    const h = raw.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return '#ffffff';
}

function rowHasNumericColumn(row, column) {
  if (!column || !row || !Object.prototype.hasOwnProperty.call(row, column)) return false;
  return Number.isFinite(Number(row[column]));
}

/** Alinea ejes X/Y con las columnas reales del resultado SQL. */
export function resolveImageDensityAxisColumns(data, { xColumn, yColumn } = {}) {
  const sample = Array.isArray(data) ? data.find((row) => row && typeof row === 'object') : null;
  if (!sample) {
    return {
      xColumn: xColumn || 'x',
      yColumn: yColumn || 'y',
    };
  }

  if (rowHasNumericColumn(sample, xColumn) && rowHasNumericColumn(sample, yColumn)) {
    return { xColumn, yColumn };
  }

  if (rowHasNumericColumn(sample, 'x') && rowHasNumericColumn(sample, 'y')) {
    return { xColumn: 'x', yColumn: 'y' };
  }

  const numericKeys = Object.keys(sample).filter(
    (k) => !isHeatmapPositionColumn(k) && rowHasNumericColumn(sample, k)
  );
  if (numericKeys.length >= 2) {
    return { xColumn: numericKeys[0], yColumn: numericKeys[1] };
  }

  return {
    xColumn: xColumn || 'x',
    yColumn: yColumn || 'y',
  };
}

function vizFieldsToImageDensityConfig(viz = {}) {
  return {
    backgroundImageUrl: viz.backgroundImageUrl || '',
    xColumn: viz.xField || 'x',
    yColumn: viz.yField || 'y',
    renderMode: viz.densityRenderMode || 'heatmap',
    heatmapStyle: viz.densityHeatmapStyle || 'smooth',
    intensity: viz.densityIntensity != null ? viz.densityIntensity : 1,
    overlayOpacity: viz.densityOverlayOpacity != null ? viz.densityOverlayOpacity : 1,
    blur: viz.densityBlur != null ? viz.densityBlur : 7,
    pointSize: viz.densityPointSize != null ? viz.densityPointSize : 1,
    separation: viz.densitySeparation != null ? viz.densitySeparation : 0.55,
    backgroundDim: viz.densityBackgroundDim != null ? viz.densityBackgroundDim : 0.72,
    groupByColumn: viz.densityGroupByColumn || '',
    weightColumn: viz.densityWeightColumn || '',
    showLegend: viz.densityShowLegend !== false,
    pointRadius:
      viz.densityPointRadius != null
        ? viz.densityPointRadius
        : viz.pointRadius != null
          ? viz.pointRadius
          : 14,
    selectedGroups: viz.densitySelectedGroups !== undefined ? viz.densitySelectedGroups : null,
    clusterEnabled: viz.densityClusterEnabled === true,
    clusterRadiusPx: viz.densityClusterRadiusPx != null ? viz.densityClusterRadiusPx : 25,
    clusterMerge: viz.densityClusterMerge || 'count',
    clusterShowCount: viz.densityClusterShowCount === true,
    clusterColorByCount: viz.densityClusterColorByCount === true,
    tooltipEnabled: viz.densityTooltipEnabled === true,
    tooltipColumns: Array.isArray(viz.densityTooltipColumns) ? viz.densityTooltipColumns : [],
    tooltipDetail: viz.densityTooltipDetail && typeof viz.densityTooltipDetail === 'object' ? viz.densityTooltipDetail : null,
    positionMode: viz.densityPositionMode === 'manual' ? 'manual' : 'columns',
    positionKeyColumn: viz.densityPositionKeyColumn || '',
    manualPositions:
      viz.densityManualPositions && typeof viz.densityManualPositions === 'object'
        ? viz.densityManualPositions
        : {},
    metricColumns: Array.isArray(viz.densityMetricColumns) ? viz.densityMetricColumns : [],
    showMarkerLabels: viz.densityShowMarkerLabels === true,
    markerEnumColumn: viz.densityMarkerEnumColumn || '',
    markerShape: normalizeMarkerShape(viz.densityMarkerShape),
    markerValueColumn: viz.densityMarkerValueColumn || '',
    markerValueAggregation: normalizeMarkerValueAggregation(viz.densityMarkerValueAggregation),
    markerLabelColumn: viz.densityMarkerLabelColumn || '',
    markerFill: viz.densityMarkerFill === 'none' ? 'none' : 'value',
    markerFit: normalizeMarkerFit(viz.densityMarkerFit),
    markerSize: normalizeMarkerSize(viz.densityMarkerSize),
    markerColor: normalizeMarkerColor(viz.densityMarkerColor),
    imageWidth: viz.densityImageWidth,
    imageHeight: viz.densityImageHeight,
    positionColumnsCommitted:
      viz.densityPositionColumnsCommitted === true ||
      isNonEmptyManualPositions(viz.densityManualPositions),
  };
}

function inferAxisFromFieldKeys(fieldKeys = [], axis) {
  if (!Array.isArray(fieldKeys) || !fieldKeys.length) return null;
  if (axis === 'x') {
    return fieldKeys.find((k) => k === 'x') || fieldKeys[0] || null;
  }
  return fieldKeys.find((k) => k === 'y') || fieldKeys[1] || fieldKeys[0] || null;
}

/** Config completa del heatmap sobre imagen: viz spec + chart.imageDensityConfig + ejes del chart. */
export function resolveImageDensityConfig(chart, data) {
  if (!chart) return { ...DEFAULT_IMAGE_DENSITY };

  const viz = chart.visualizationSpec?.visualization || {};
  const fieldKeys =
    chart.visualizationSpec?.fieldKeys ||
    chart.fieldKeys ||
    viz.fieldKeys ||
    [];
  const fromViz = vizFieldsToImageDensityConfig(viz);
  const stored = {
    ...(chart.advancedConfig?.imageDensityConfig || {}),
    ...(chart.imageDensityConfig || {}),
  };

  const configuredX = stored.xColumn || chart.xColumn || fromViz.xColumn || inferAxisFromFieldKeys(fieldKeys, 'x') || 'x';
  const configuredY = stored.yColumn || chart.yColumn || fromViz.yColumn || inferAxisFromFieldKeys(fieldKeys, 'y') || 'y';
  const positionMode =
    stored.positionMode === 'manual' || fromViz.positionMode === 'manual' ? 'manual' : 'columns';
  const axes =
    positionMode === 'manual'
      ? { xColumn: configuredX, yColumn: configuredY }
      : resolveImageDensityAxisColumns(data, {
          xColumn: configuredX,
          yColumn: configuredY,
        });
  const merged = {
    ...DEFAULT_IMAGE_DENSITY,
    ...fromViz,
    ...stored,
  };
  const explicitLabels = stored.showMarkerLabels ?? fromViz.showMarkerLabels;
  const showMarkerLabels =
    explicitLabels !== undefined ? explicitLabels === true : positionMode === 'manual';

  return {
    ...merged,
    backgroundImageUrl: stored.backgroundImageUrl || fromViz.backgroundImageUrl || '',
    xColumn: axes.xColumn,
    yColumn: axes.yColumn,
    positionMode,
    positionKeyColumn: inferPositionKeyColumn(
      data,
      merged.manualPositions,
      merged.positionKeyColumn || ''
    ),
    manualPositions:
      merged.manualPositions && typeof merged.manualPositions === 'object'
        ? merged.manualPositions
        : {},
    metricColumns: Array.isArray(merged.metricColumns) ? merged.metricColumns : [],
    showMarkerLabels,
    positionColumnsCommitted:
      stored.positionColumnsCommitted === true ||
      fromViz.positionColumnsCommitted === true ||
      isNonEmptyManualPositions(merged.manualPositions),
    showLegend:
      stored.showLegend !== undefined
        ? stored.showLegend !== false
        : fromViz.showLegend !== false,
  };
}

export function imageDensityVizFieldsFromConfig(cfg = {}) {
  return {
    densityRenderMode: cfg.renderMode || 'heatmap',
    densityHeatmapStyle: cfg.heatmapStyle || 'smooth',
    densityIntensity: cfg.intensity != null ? cfg.intensity : 1,
    densityOverlayOpacity: cfg.overlayOpacity != null ? cfg.overlayOpacity : 1,
    densityBlur: cfg.blur != null ? cfg.blur : 7,
    densityPointSize: cfg.pointSize != null ? cfg.pointSize : 1,
    densitySeparation: cfg.separation != null ? cfg.separation : 0.55,
    densityBackgroundDim: cfg.backgroundDim != null ? cfg.backgroundDim : 0.72,
    densityGroupByColumn: cfg.groupByColumn || '',
    densityWeightColumn: cfg.weightColumn || '',
    densityShowLegend: cfg.showLegend !== false,
    densityPointRadius: cfg.pointRadius != null ? cfg.pointRadius : 14,
    densitySelectedGroups: cfg.selectedGroups ?? null,
    densityClusterEnabled: cfg.clusterEnabled === true,
    densityClusterRadiusPx: cfg.clusterRadiusPx != null ? cfg.clusterRadiusPx : 25,
    densityClusterMerge: cfg.clusterMerge || 'count',
    densityClusterShowCount: cfg.clusterShowCount === true,
    densityClusterColorByCount: cfg.clusterColorByCount === true,
    densityTooltipEnabled: cfg.tooltipEnabled === true,
    densityTooltipColumns: Array.isArray(cfg.tooltipColumns) ? cfg.tooltipColumns : [],
    densityTooltipDetail: cfg.tooltipDetail && typeof cfg.tooltipDetail === 'object' ? cfg.tooltipDetail : null,
    densityPositionMode: cfg.positionMode === 'manual' ? 'manual' : 'columns',
    densityPositionKeyColumn: cfg.positionKeyColumn || '',
    densityManualPositions:
      cfg.manualPositions && typeof cfg.manualPositions === 'object' ? cfg.manualPositions : {},
    densityMetricColumns: Array.isArray(cfg.metricColumns) ? cfg.metricColumns : [],
    densityShowMarkerLabels: cfg.showMarkerLabels === true,
    densityMarkerEnumColumn: cfg.markerEnumColumn || '',
    densityMarkerShape: normalizeMarkerShape(cfg.markerShape),
    densityMarkerValueColumn: cfg.markerValueColumn || '',
    densityMarkerValueAggregation: normalizeMarkerValueAggregation(cfg.markerValueAggregation),
    densityMarkerLabelColumn: cfg.markerLabelColumn || '',
    densityMarkerFill: cfg.markerFill === 'none' ? 'none' : 'value',
    densityMarkerFit: normalizeMarkerFit(cfg.markerFit),
    densityMarkerSize: normalizeMarkerSize(cfg.markerSize),
    densityMarkerColor: normalizeMarkerColor(cfg.markerColor),
    densityImageWidth: cfg.imageWidth,
    densityImageHeight: cfg.imageHeight,
    densityPositionColumnsCommitted:
      cfg.positionColumnsCommitted === true || isNonEmptyManualPositions(cfg.manualPositions),
  };
}

export function buildImageDensityConfigFromViz(viz = {}, overrides = {}) {
  return {
    ...vizFieldsToImageDensityConfig(viz),
    ...overrides,
  };
}

export function isNonEmptyManualPositions(value) {
  return Boolean(
    value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 0
  );
}

/** Posiciones confirmadas del heatmap, aunque el gráfico ya no sea imageDensity. */
export function resolveCommittedHeatmapPositions(source, data) {
  if (!source || typeof source !== 'object') return null;
  const viz =
    source.visualization ||
    source.visualizationSpec?.visualization ||
    {};
  const cfg = {
    ...(source.advancedConfig?.imageDensityConfig || {}),
    ...(source.imageDensityConfig || {}),
  };
  const positions = coalesceManualPositions(
    source.manualPositions,
    coalesceManualPositions(cfg.manualPositions, viz.densityManualPositions)
  );
  const committed =
    source.positionColumnsCommitted === true ||
    cfg.positionColumnsCommitted === true ||
    viz.densityPositionColumnsCommitted === true ||
    isNonEmptyManualPositions(positions);
  if (!committed) return null;
  const preferred =
    source.positionKeyColumn ||
    cfg.positionKeyColumn ||
    viz.densityPositionKeyColumn ||
    '';
  const positionKeyColumn = inferPositionKeyColumn(data, positions, preferred);
  if (!positionKeyColumn) return null;
  return { positionKeyColumn, positions };
}

export function applyCommittedHeatmapPositionColumns(data, source) {
  const resolved = resolveCommittedHeatmapPositions(source, data);
  if (!resolved) return data || [];
  return attachHeatmapPositionColumns(data, resolved);
}

export function coalesceManualPositions(preferred, fallback) {
  if (isNonEmptyManualPositions(preferred)) return preferred;
  if (isNonEmptyManualPositions(fallback)) return fallback;
  if (preferred && typeof preferred === 'object' && !Array.isArray(preferred)) return preferred;
  if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) return fallback;
  return {};
}

export function imageDensityConfigFromChart(chart) {
  if (!chart || typeof chart !== 'object') return {};
  return {
    ...(chart.advancedConfig?.imageDensityConfig || {}),
    ...(chart.imageDensityConfig || {}),
  };
}

function vizManualLayout(chart) {
  const viz = chart?.visualizationSpec?.visualization;
  if (!viz || typeof viz !== 'object') return {};
  return {
    ...(viz.densityManualPositions != null ? { manualPositions: viz.densityManualPositions } : {}),
    ...(viz.densityPositionMode != null ? { positionMode: viz.densityPositionMode } : {}),
    ...(viz.densityPositionKeyColumn != null ? { positionKeyColumn: viz.densityPositionKeyColumn } : {}),
    ...(viz.densityImageWidth != null ? { imageWidth: viz.densityImageWidth } : {}),
    ...(viz.densityImageHeight != null ? { imageHeight: viz.densityImageHeight } : {}),
    ...(viz.densityPositionColumnsCommitted != null
      ? { positionColumnsCommitted: viz.densityPositionColumnsCommitted }
      : {}),
  };
}

/** Evita que un publish/propagate con mapa vacío borre puntos ya colocados. */
export function withPreservedManualPositions(incomingChart, existingChart) {
  const incoming = incomingChart && typeof incomingChart === 'object' ? incomingChart : {};
  const previousCfg = imageDensityConfigFromChart(existingChart);
  const incomingCfg = imageDensityConfigFromChart(incoming);
  const incomingViz = vizManualLayout(incoming);
  const previousViz = vizManualLayout(existingChart);
  const manualPositions = coalesceManualPositions(
    coalesceManualPositions(incomingCfg.manualPositions, incomingViz.manualPositions),
    coalesceManualPositions(previousCfg.manualPositions, previousViz.manualPositions)
  );
  const positionMode =
    incomingCfg.positionMode === 'manual' ||
    incomingViz.positionMode === 'manual' ||
    previousCfg.positionMode === 'manual' ||
    previousViz.positionMode === 'manual'
      ? 'manual'
      : incomingCfg.positionMode || previousCfg.positionMode || 'columns';
  const positionKeyColumn =
    incomingCfg.positionKeyColumn ||
    incomingViz.positionKeyColumn ||
    previousCfg.positionKeyColumn ||
    previousViz.positionKeyColumn ||
    '';
  // El builder deja la imagen nueva en visualization.backgroundImageUrl; si no se
  // prioriza, la del chart guardado la pisa y el ancho/alto viejo descuadra los marcadores.
  const incomingBg =
    incoming.visualizationSpec?.visualization?.backgroundImageUrl || incomingCfg.backgroundImageUrl || '';
  const previousBg =
    previousCfg.backgroundImageUrl || existingChart?.visualizationSpec?.visualization?.backgroundImageUrl || '';
  const imageChanged = Boolean(incomingBg) && incomingBg !== previousBg;
  const imageWidth = imageChanged
    ? incomingCfg.imageWidth ?? incomingViz.imageWidth
    : incomingCfg.imageWidth ?? incomingViz.imageWidth ?? previousCfg.imageWidth ?? previousViz.imageWidth;
  const imageHeight = imageChanged
    ? incomingCfg.imageHeight ?? incomingViz.imageHeight
    : incomingCfg.imageHeight ?? incomingViz.imageHeight ?? previousCfg.imageHeight ?? previousViz.imageHeight;
  const nextIdCfg = {
    ...previousCfg,
    ...incomingCfg,
    ...(incomingBg ? { backgroundImageUrl: incomingBg } : {}),
    ...(imageChanged && imageWidth == null ? { imageWidth: undefined } : {}),
    ...(imageChanged && imageHeight == null ? { imageHeight: undefined } : {}),
    manualPositions,
    positionMode,
    positionKeyColumn,
    positionColumnsCommitted:
      previousCfg.positionColumnsCommitted === true ||
      incomingCfg.positionColumnsCommitted === true ||
      isNonEmptyManualPositions(manualPositions),
    ...(imageWidth != null ? { imageWidth } : {}),
    ...(imageHeight != null ? { imageHeight } : {}),
  };
  const vizSpec = incoming.visualizationSpec || existingChart?.visualizationSpec;
  const viz = vizSpec?.visualization;
  const nextVizSpec = viz
    ? {
        ...vizSpec,
        visualization: {
          ...viz,
          densityManualPositions: manualPositions,
          densityPositionMode: positionMode === 'manual' ? 'manual' : viz.densityPositionMode,
          densityPositionKeyColumn: positionKeyColumn || viz.densityPositionKeyColumn,
          densityPositionColumnsCommitted:
            nextIdCfg.positionColumnsCommitted === true || viz.densityPositionColumnsCommitted,
          ...(imageWidth != null ? { densityImageWidth: imageWidth } : {}),
          ...(imageHeight != null ? { densityImageHeight: imageHeight } : {}),
        },
      }
    : vizSpec;

  return {
    ...incoming,
    imageDensityConfig: { ...(incoming.imageDensityConfig || {}), ...nextIdCfg },
    advancedConfig: {
      ...(incoming.advancedConfig || {}),
      imageDensityConfig: {
        ...(incoming.advancedConfig?.imageDensityConfig || incoming.imageDensityConfig || {}),
        ...nextIdCfg,
      },
    },
    visualizationSpec: nextVizSpec,
  };
}
